/**
 * RelayRecipientDetailPage
 * Per-address recipient health detail with associated cases.
 * Route: /settings/agent-email/recipients/:address (inside SettingsAgentEmailPage)
 * US-5
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { fetchRecipientDetail, type RecipientDetailResponse } from '../lib/relay.api';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { RecipientHealthBadge } from '../components/relay/RecipientHealthBadge';
import { ChevronLeft } from 'lucide-react';

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString([], {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

function StatItem({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="p-3 bg-muted/50 rounded-md">
      <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">{label}</p>
      <p className="text-lg font-semibold text-foreground tabular-nums">{value}</p>
    </div>
  );
}

export function RelayRecipientDetailPage() {
  const { address } = useParams<{ address: string }>();
  const navigate = useNavigate();

  const [recipient, setRecipient] = useState<RecipientDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadRecipient = useCallback(async () => {
    if (!address) return;
    try {
      setLoading(true);
      setError(null);
      const data = await fetchRecipientDetail(address);
      setRecipient(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load recipient');
    } finally {
      setLoading(false);
    }
  }, [address]);

  useEffect(() => {
    loadRecipient();
  }, [loadRecipient]);

  return (
    <div data-testid="relay-recipient-detail-page">
      {/* Back navigation */}
      <button
        onClick={() => navigate('/settings/agent-email/recipients')}
        className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4 min-h-[44px] transition-colors"
        data-testid="recipient-detail-back"
      >
        <ChevronLeft className="h-4 w-4" />
        Back to Recipients
      </button>

      {/* Loading state */}
      {loading && (
        <div data-testid="recipient-detail-loading" className="space-y-4">
          <Skeleton className="h-8 w-80" />
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      )}

      {/* Error state */}
      {error && (
        <div
          data-testid="recipient-detail-error"
          className="p-3 bg-destructive/10 border border-destructive/30 rounded-md text-destructive text-sm flex items-center justify-between gap-3"
          role="alert"
        >
          <span>{error}</span>
          <Button
            variant="outline"
            size="sm"
            onClick={loadRecipient}
            data-testid="recipient-detail-retry"
          >
            Retry
          </Button>
        </div>
      )}

      {/* Content */}
      {!loading && !error && recipient && (
        <>
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-start gap-3 flex-wrap">
              <div>
                <h1 className="text-xl font-semibold text-foreground font-mono break-all">
                  {recipient.address}
                </h1>
                <p className="text-sm text-muted-foreground mt-1 capitalize">
                  Kind: {recipient.kind}
                </p>
              </div>
              <RecipientHealthBadge tier={recipient.healthTier} className="mt-1" />
            </div>
          </div>

          {/* Health summary card */}
          <div
            data-testid="recipient-health-summary"
            className="rounded-lg border border-border bg-card p-4 mb-6"
          >
            <h2 className="text-sm font-medium text-muted-foreground mb-3 uppercase tracking-wide">
              Health Summary
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
              <StatItem label="Bounces" value={recipient.bounceCount} />
              <StatItem label="Complaints" value={recipient.complaintCount} />
              <StatItem label="Outbound" value={recipient.outboundCount} />
              <StatItem label="Inbound" value={recipient.inboundCount} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-muted-foreground">Last Inbound: </span>
                <span className="text-foreground">{formatDate(recipient.lastInboundAt)}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Last Outbound: </span>
                <span className="text-foreground">{formatDate(recipient.lastOutboundAt)}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Last Updated: </span>
                <span className="text-foreground">{formatDate(recipient.updatedAt)}</span>
              </div>
            </div>
          </div>

          {/* Associated cases */}
          <div data-testid="recipient-cases">
            <h2 className="text-sm font-medium text-muted-foreground mb-3 uppercase tracking-wide">
              Associated Cases ({recipient.recentCases.length})
            </h2>
            {recipient.recentCases.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4">No associated cases.</p>
            ) : (
              <div className="rounded-lg border border-border overflow-hidden">
                <table className="w-full text-sm" aria-label="Associated cases">
                  <thead>
                    <tr className="bg-muted/50 border-b border-border">
                      <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                        Case ID
                      </th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                        Kind
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {recipient.recentCases.map((c, index) => (
                      <tr
                        key={c.id}
                        data-testid={`recipient-case-${c.id}`}
                        className={`hover:bg-accent transition-colors ${
                          index < recipient.recentCases.length - 1
                            ? 'border-b border-border'
                            : ''
                        }`}
                      >
                        <td className="px-4 py-3 font-mono text-xs text-foreground">
                          <Link
                            to={`/repairs/${c.id}`}
                            className="hover:underline focus:outline-none focus:ring-2 focus:ring-ring rounded"
                            aria-label={`View case ${c.id}`}
                          >
                            {c.id}
                          </Link>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground capitalize">
                          {c.kind}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
